import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ax/Button";
import { DecisionCard } from "@/components/ax/DecisionCard";
import { EPISTEMIC, EpistemicBadge, EpistemicRow } from "@/components/ax/EpistemicRow";
import { PrimaryBar } from "@/components/ax/PrimaryBar";
import { Brief } from "@/components/brief/Brief";
import { example } from "@/data/example";
import { useI18n } from "@/i18n";

export default function Contoh() {
  const { t, lang } = useI18n();
  const navigate = useNavigate();
  const ex = example[lang];
  const chosen = ex.options.find((o) => o.id === ex.decision.optionId);
  const recommended = ex.options.find((o) => o.id === ex.brief.recommendation.optionId);
  const differs = chosen.id !== recommended.id;

  return (
    <div data-testid="example-page">
      <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
        <span
          data-testid="example-badge"
          className="inline-flex h-6 items-center rounded-[8px] border border-ax-muted px-2 font-mono text-[13px] uppercase leading-[18px] text-ax-text-2"
        >
          {t("example.badge")}
        </span>
        <span className="font-mono text-[13px] leading-[18px] text-ax-muted">
          {t("decisions.horizon", { n: ex.horizonDays })}
        </span>
      </div>
      <h1 className="type-h1 mt-3 [overflow-wrap:anywhere]">{ex.title}</h1>
      <p className="m-0 mt-2 text-base leading-6 text-ax-text-2" data-testid="example-question">
        {ex.question}
      </p>
      <p className="m-0 mt-3 text-sm leading-5 text-ax-muted" data-testid="example-note">{t("example.note")}</p>

      <div className="mt-8">
        <DecisionCard optionName={chosen.name} rationale={ex.decision.rationale} />
        {differs && (
          <p data-testid="differ-note" className="m-0 mt-3 text-sm leading-5 text-ax-text-2">
            {t("commit.differ")}
          </p>
        )}
      </div>

      <section className="mt-12" aria-labelledby="ex-facts">
        <h2 id="ex-facts" className="caption m-0 mb-3 text-ax-muted">{t("layer.facts")}</h2>
        <div className="grid gap-6 md:grid-cols-3">
          {EPISTEMIC.map((ep) => (
            <div key={ep} data-testid={`facts-group-${ep.toLowerCase()}`}>
              <ul className="m-0 flex list-none flex-col gap-3 p-0">
                {ex.items
                  .filter((i) => i.epistemic === ep)
                  .map((i) => (
                    <EpistemicRow key={i.id} item={i} readOnly />
                  ))}
              </ul>
            </div>
          ))}
        </div>
      </section>

      <section className="mt-12" aria-labelledby="ex-options">
        <h2 id="ex-options" className="caption m-0 mb-3 text-ax-muted">{t("opt.list")}</h2>
        <ul className="m-0 grid list-none gap-3 p-0 md:grid-cols-3">
          {ex.options.map((o) => (
            <li
              key={o.id}
              data-testid={`example-option-${o.id}`}
              className="rounded-[12px] border border-ax-border bg-ax-surface p-4 shadow"
            >
              <p className="m-0 text-base font-medium leading-6 text-ax-text">{o.name}</p>
              <p className="m-0 mt-2 font-mono text-[13px] leading-[18px] text-ax-muted">{t(`rev.${o.reversibility}`)}</p>
            </li>
          ))}
        </ul>
      </section>

      <section className="mt-12" aria-labelledby="ex-brief">
        <div className="mb-3 flex flex-wrap items-baseline gap-x-3 gap-y-1">
          <h2 id="ex-brief" className="caption m-0 text-ax-muted">{t("brief.title")}</h2>
          <span className="font-mono text-[13px] leading-[18px] text-ax-muted">v{ex.brief.version}</span>
          <span className="font-mono text-[13px] leading-[18px] text-ax-muted">{t("example.briefNote")}</span>
        </div>
        <Brief brief={ex.brief} items={ex.items} options={ex.options} />
      </section>

      <PrimaryBar>
        <Button
          variant="primary"
          onClick={() => navigate("/keputusan/baru")}
          data-testid="example-back-button"
          className="w-full md:w-auto md:min-w-[200px]"
        >
          {t("example.back")}
        </Button>
      </PrimaryBar>
    </div>
  );
}
